import { defineStore } from "pinia";

import counterpartiesApi from "@/services/api/counterpartiesApi";
import driversApi from "@/services/api/driversApi";
import edoDocumentsApi from "@/services/api/edoDocumentsApi";
import orderPointsApi from "@/services/api/orderPointsApi";
import ordersApi from "@/services/api/ordersApi";
import tripsApi from "@/services/api/tripsApi";
import vehicleDocumentsApi from "@/services/api/vehicleDocumentsApi";
import vehiclesApi from "@/services/api/vehiclesApi";

import { getCompanyId } from "@/utils/userUtils";
import { useCounterpartyStore } from "@/stores/counterpartyStore";
import { useDriverStore } from "@/stores/driverStore";
import { useOrderPointStore } from "@/stores/orderPointStore";
import { useVehicleStore } from "@/stores/vehicleStore";

// Store для поиска и кеширования сущностей, нужных ЭДО-формам при предзаполнении.
// Не хранит состояние документов — только справочники и cache-or-fetch хелперы.
export const useEdoEntityStore = defineStore("edoEntityStore", {
  state: () => ({
    companyId: getCompanyId(),
    vehicleTypeOptions: [],
    vehicleOwnershipTypeOptions: [],
    ownershipDocumentsByVehicleId: {},
    isVehicleTypesLoading: false,
    isVehicleOwnershipTypesLoading: false,
  }),

  actions: {
    // Загружает справочник типов ТС для ЭДО один раз и кеширует
    async fetchVehicleTypes() {
      if (this.vehicleTypeOptions.length > 0) return this.vehicleTypeOptions;

      this.isVehicleTypesLoading = true;
      try {
        const response = await edoDocumentsApi.vehicleTypes(this.companyId);
        this.vehicleTypeOptions = response.data.result || [];
        return this.vehicleTypeOptions;
      } finally {
        this.isVehicleTypesLoading = false;
      }
    },

    // Загружает справочник типов владения ТС для ЭДО один раз и кеширует
    async fetchVehicleOwnershipTypes() {
      if (this.vehicleOwnershipTypeOptions.length > 0) return this.vehicleOwnershipTypeOptions;

      this.isVehicleOwnershipTypesLoading = true;
      try {
        const response = await edoDocumentsApi.vehicleOwnershipTypes(this.companyId);
        this.vehicleOwnershipTypeOptions = response.data.result || [];
        return this.vehicleOwnershipTypeOptions;
      } finally {
        this.isVehicleOwnershipTypesLoading = false;
      }
    },

    // Загружает документы-основания ТС (ownership_basis) и кеширует по vehicleId
    async fetchVehicleOwnershipDocuments(vehicleId) {
      if (!vehicleId) return [];
      if (this.ownershipDocumentsByVehicleId[vehicleId]) {
        return this.ownershipDocumentsByVehicleId[vehicleId];
      }

      const response = await vehicleDocumentsApi.index(this.companyId, vehicleId);
      const all = response.data.result || [];
      const docs = all.filter((d) => d.document_kind === "ownership_basis");
      this.ownershipDocumentsByVehicleId = {
        ...this.ownershipDocumentsByVehicleId,
        [vehicleId]: docs,
      };
      return docs;
    },

    // Ищет контрагента в кеше counterpartyStore или загружает через API
    async fetchCounterparty(id) {
      const cached = useCounterpartyStore().counterparties.find((item) => String(item.id) === String(id));
      if (cached) return cached;

      const response = await counterpartiesApi.show(this.companyId, id);
      return response.data.result;
    },

    // Ищет контрагента по точному ИНН для восстановления селекта из parsed_data ЭДО
    async findCounterpartyByInn(inn) {
      const normalizedInn = String(inn || "").trim();
      if (!normalizedInn) return null;

      const store = useCounterpartyStore();
      // Автоподстановка по ИНН выбирает только включенного контрагента.
      // Отключенные записи можно показывать по id в старых связях, но нельзя
      // незаметно подставлять в новый ЭДО-документ.
      const cached = store.counterparties.find(
        (item) =>
          item.enabled !== false &&
          String(item.inn || "").trim() === normalizedInn
      );
      if (cached) return cached;

      const response = await counterpartiesApi.index(this.companyId, {
        "q[inn]": normalizedInn,
        "q[enabled]": "true",
        limit: 10,
      });
      const counterparty = (response.data.result || []).find(
        (item) => String(item.inn || "").trim() === normalizedInn
      );

      if (!counterparty) return null;

      const exists = store.counterparties.find(
        (item) => item.id === counterparty.id
      );
      if (!exists) {
        store.counterparties = [...store.counterparties, counterparty];
      }

      return counterparty;
    },

    // Загружает полный заказ для предзаполнения ЭДО.
    async fetchOrder(id) {
      const response = await ordersApi.show(this.companyId, id);
      return response.data.result;
    },

    // Ищет точку заказа в кеше orderPointStore или загружает через API
    async fetchOrderPoint(orderId, pointId) {
      const cached = useOrderPointStore().orderPoints.find((item) => String(item.id) === String(pointId));
      if (cached) return cached;

      const response = await orderPointsApi.show(this.companyId, orderId, pointId);
      return response.data.result;
    },

    // Загружает рейс по id для предзаполнения транспорта и водителей
    async fetchTrip(id) {
      const response = await tripsApi.show(this.companyId, id);
      return response.data.result;
    },

    // Загружает список рейсов, например один рейс по заказу
    async fetchTrips(params = {}) {
      const response = await tripsApi.index(this.companyId, params);
      return response.data?.result || [];
    },

    // Ищет транспорт в кеше vehicleStore или загружает через API
    async fetchVehicle(id) {
      const cached = useVehicleStore().vehicles.find((item) => String(item.id) === String(id));
      if (cached) return cached;

      const response = await vehiclesApi.show(this.companyId, id);
      return response.data.result;
    },

    // Ищет водителя в кеше driverStore или загружает через API.
    // Кеш принимается только если содержит driver_license: список-endpoint
    // отдаёт сокращённый объект без данных ВУ, show-endpoint — полный.
    async fetchDriver(id) {
      const cached = useDriverStore().drivers.find((item) => String(item.id) === String(id));
      if (cached && Object.prototype.hasOwnProperty.call(cached, "driver_license")) {
        return cached;
      }

      const response = await driversApi.show(this.companyId, id);
      return response.data.result;
    },
  },
});
